import React from 'react'

import { QUESTION_STATUSES } from '../model'
import { useEditFormState } from './useEditFormState'
import { useStepsState } from './useStepsState'

// NOTE: hook that groups up useStepsState and useEditFormState
// to expose common state and handlers for the interview template
// creator components
export const useInternals = (state, handlers) => {
  const { sections, templateUrl } = state
  const {
    handleDeleteTemplateQuestion,
    handlePatchTemplateQuestion,
    handlePatchTemplateSection,
    handlePostTemplateQuestion,
    handlePostTemplateSection,
    handleSetResponse,
    handleShowSnackbar,
  } = handlers

  const {
    state: {
      activeSection,
      activeStep,
      currentStepIndex,
      questionsCount,
      sectionsCount,
      steps,
      isCategoryScorable,
      isCategoryDeletable,
      isEditMode,
      isIdleMode,
      isInsertedCategory,
      isNextCategoryDisabled,
      isNextStepDisabled,
      isPreviousCategoryDisabled,
      isPreviousStepDisabled,
      isStaticPage,
    },
    handlers: {
      handleChangeNextStep,
      handleChangePreviousStep,
      handleChangeMode,
      handleChangeStep,
      handleInsertCategory,
      handleInsertCategoryCancel,
    },
  } = useStepsState({ sections }, { handleSetResponse })

  const {
    state: { edit, hasNoQuestions },
    handlers: {
      handleAppendQuestion,
      handleRemoveQuestion,
      handleUpdateQuestion,
      handleFormReset,
      handleFormSubmit,
      watch,
    },
  } = useEditFormState(
    { activeSection, currentStepIndex, templateUrl, isInsertedCategory },
    {
      handleChangeMode,
      handleChangeStep,
      handleDeleteTemplateQuestion,
      handlePatchTemplateQuestion,
      handlePatchTemplateSection,
      handlePostTemplateQuestion,
      handlePostTemplateSection,
      handleSetResponse,
      handleShowSnackbar,
    },
  )

  const handleEditCategoryCancel = React.useCallback(() => {
    isInsertedCategory ? handleInsertCategoryCancel() : handleChangeMode()
    handleFormReset()
  }, [
    handleChangeMode,
    handleInsertCategoryCancel,
    handleFormReset,
    isInsertedCategory,
  ])

  const handleEditCategoryConfirm = React.useCallback(handleFormSubmit, [
    handleFormSubmit,
  ])

  const handleEditCategoryOpen = React.useCallback(handleChangeMode, [
    handleChangeMode,
  ])

  const handleDeleteQuestion = React.useCallback(
    index => {
      handleRemoveQuestion(index)
    },
    [handleRemoveQuestion],
  )

  const handleInsertQuestion = React.useCallback(() => {
    handleAppendQuestion({
      description: '',
      question: '',
      status: QUESTION_STATUSES.INSERTED,
      uuid: null,
    })
  }, [handleAppendQuestion])

  const handleUpgradeQuestion = React.useCallback(
    (index, value) => {
      handleUpdateQuestion(index, value)
    },
    [handleUpdateQuestion],
  )

  React.useEffect(() => {
    if (isInsertedCategory) {
      handleInsertQuestion()
    }
  }, [handleInsertQuestion, isInsertedCategory])

  return {
    state: {
      activeSection,
      activeStep,
      currentStepIndex,
      edit,
      questionsCount,
      sectionsCount,
      steps,
      hasNoQuestions,
      isCategoryScorable,
      isCategoryDeletable,
      isEditMode,
      isIdleMode,
      isNextCategoryDisabled,
      isNextStepDisabled,
      isPreviousCategoryDisabled,
      isPreviousStepDisabled,
      isStaticPage,
    },
    handlers: {
      handleChangeNextStep,
      handleChangePreviousStep,
      handleChangeStep,
      handleChangeMode,
      handleDeleteQuestion,
      handleEditCategoryCancel,
      handleEditCategoryConfirm,
      handleEditCategoryOpen,
      handleInsertCategory,
      handleInsertQuestion,
      handleUpgradeQuestion,
      watch,
    },
  }
}
